import {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  NodeKey,
  SerializedTextNode,
  Spread,
  TextNode
} from 'lexical';

export interface MentionPayload {
  mentionAttachment: string;
}

export type SerializedMentionNode = Spread<
  { mentionAttachment: string },
  SerializedTextNode
>;

export class MentionNode extends TextNode {
  __mentionAttachment: string;

  static override getType(): string {
    return 'mention';
  }

  static override clone(node: MentionNode): MentionNode {
    return new MentionNode(
      { mentionAttachment: node.__mentionAttachment },
      node.__key
    );
  }

  constructor(payload: MentionPayload, key?: NodeKey) {
    const doc = new DOMParser().parseFromString(
      payload.mentionAttachment,
      'text/html'
    );
    const text = doc.body.textContent?.replace(/\s+/g, ' ').trim() || '@';
    super(text, key);
    this.__mentionAttachment = payload.mentionAttachment;
  }

  override createDOM(): HTMLElement {
    // Return simple element for Lexical to track cursor properly
    const anchor = document.createElement('a');
    anchor.className = 'user-mention';
    anchor.textContent = this.getTextContent();
    return anchor;
  }

  override exportDOM(): DOMExportOutput {
    const doc = new DOMParser().parseFromString(
      this.__mentionAttachment,
      'text/html'
    );
    return { element: doc.body.firstElementChild as HTMLElement };
  }

  static override importDOM(): DOMConversionMap | null {
    return {
      'action-text-attachment': () => ({
        conversion: (element: HTMLElement): DOMConversionOutput | null => {
          if (!element.querySelector('a.user-mention')) return null;
          return {
            node: $createMentionNode({ mentionAttachment: element.outerHTML })
          };
        },
        priority: 1 as const
      })
    };
  }

  override exportJSON(): SerializedMentionNode {
    return {
      ...super.exportJSON(),
      type: 'mention',
      mentionAttachment: this.__mentionAttachment
    };
  }

  static override importJSON(
    serializedNode: SerializedMentionNode
  ): MentionNode {
    return $createMentionNode({
      mentionAttachment: serializedNode.mentionAttachment
    });
  }

  override isTextEntity(): true {
    return true;
  }

  override canInsertTextBefore(): boolean {
    return false;
  }

  override canInsertTextAfter(): boolean {
    return false;
  }
}

export function $createMentionNode(payload: MentionPayload): MentionNode {
  return new MentionNode(payload)
    .setMode('segmented')
    .toggleDirectionless() as MentionNode;
}
